import { Link, createFileRoute } from '@tanstack/react-router';

import { Button } from '@/common/components/elements/button';
import { Title } from '@/common/components/elements/title';
import { EmptyState } from '@/common/components/features/empty-state';
import { ProjectCard, ProjectCardSkeleton } from '@/common/components/features/project-card';
import { useGetProjectsQuery } from '@/modules/projects/queries/use-get-projects-query';

const skeletonArray = Array.from({ length: 6 });

const MyProjects = () => {
  const { data: projects = [], isLoading, error, refetch } = useGetProjectsQuery();

  return (
    <div className='flex flex-col w-full h-full gap-6'>
      <div className='flex flex-col gap-3'>
        <Title>Meus projetos</Title>
        <span className='flex items-center'>
          <span className='h-px flex-1 bg-border-neutral'></span>
        </span>
      </div>
      {error && (
        <EmptyState
          image={{
            src: '/images/empty-image.webp',
            alt: 'Ilustração representando erro ao carregar projetos - pessoas colaborando em torno de uma lâmpada quebrada',
          }}
          title='Erro ao carregar projetos'
          description='Ocorreu um problema ao buscar seus projetos. Verifique sua conexão e tente novamente.'
          actions={
            <>
              <Button
                size='large'
                variant='primary'
                onClick={() => refetch()}
                className='px-8 py-3 text-base rounded-xl transition-transform duration-200 hover:scale-[1.02]'
              >
                Tentar novamente
              </Button>

              <Link
                to='/explorar'
                aria-label='Ir para página Explorar para ver projetos disponíveis'
              >
                <Button
                  size='large'
                  variant='secondary'
                  className='px-8 py-3 text-base rounded-xl transition-transform duration-200 hover:scale-[1.02]'
                >
                  Explorar projetos
                </Button>
              </Link>
            </>
          }
        />
      )}
      {!error && !isLoading && projects.length === 0 && (
        <EmptyState
          image={{
            src: '/images/empty-image.webp',
            alt: 'Ilustração representando que não há projetos disponíveis - pessoas colaborando em torno de uma lâmpada quebrada',
          }}
          title='Nenhum projeto disponível'
          description='Acesse a aba Explorar para solicitar participação em um projeto'
          actions={
            <Link
              to='/explorar'
              aria-label='Ir para página Explorar para ver projetos disponíveis'
              className='focus:outline-none focus:ring-2 focus:ring-content-accent focus:ring-offset-2 rounded-lg'
            >
              <Button
                size='large'
                variant='primary'
                className='px-10 py-4 text-lg rounded-xl transition-transform duration-200 hover:scale-[1.02]'
              >
                Explorar
              </Button>
            </Link>
          }
        />
      )}
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-5 auto-rows-fr'>
        {isLoading
          ? skeletonArray.map((_, i) => <ProjectCardSkeleton key={i} />)
          : !error &&
            projects.map((project) => (
              <ProjectCard
                key={project.id}
                title={project.name}
                description={project.description}
                banner={{ style: 'full', alt: project.name, src: project.thumbnail }}
                action={
                  <Link
                    to='/projetos/$projeto'
                    params={{ projeto: project.id }}
                    aria-label={`Ir para projeto ${project.name}`}
                    className='focus:outline-none focus:ring-2 focus:ring-content-accent focus:ring-offset-2 rounded-lg'
                  >
                    <Button className='w-20 h-8 rounded-100 text-sm' variant='secondary'>
                      Ir
                    </Button>
                  </Link>
                }
              />
            ))}
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_app/_dashboard/projetos/')({
  head: () => ({
    meta: [
      {
        title: 'Meus Projetos | Podcast AI',
      },
    ],
  }),
  component: MyProjects,
});
